import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import ListComponent from "./ListComponent";
import { formatFloatToTimeHM } from "../../utils/timeFormatter";

// Horizontal bar chart sizing: every bar is the same height regardless of how
// many tasks there are, so the chart grows with the data instead of squeezing.
const BAR_SIZE = 22;
const ROW_HEIGHT = 34;
const LABEL_WIDTH = 170;
const LABEL_MAX_CHARS = 28;

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "");

/** Y-axis tick that shortens long task names; the full name shows in the tooltip. */
const TaskTick = ({ x, y, payload, fill }) => (
  <text x={x} y={y} dy={4} textAnchor="end" fill={fill} fontSize={12}>
    <title>{payload.value}</title>
    {truncate(payload.value, LABEL_MAX_CHARS)}
  </text>
);

const DurationTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", p: 1, borderRadius: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.task}</Typography>
      <Typography variant="body2">{formatFloatToTimeHM(row.duration)}</Typography>
    </Box>
  );
};

/**
 * Time logged per task/meeting, one horizontal bar each, longest first.
 * Durations are decimal hours (see timeFormatter). When onBarClick is given,
 * task bars (rows with a task_id) open that task; meeting bars do nothing.
 */
const TimeBarChart = ({ data, onBarClick }) => {
  const theme = useTheme();
  const rows = useMemo(
    () =>
      (data || [])
        .map((d) => ({ ...d, duration: parseFloat(d.duration) || 0 }))
        .sort((a, b) => b.duration - a.duration),
    [data]
  );

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No time logged
      </Typography>
    );
  }

  const height = rows.length * ROW_HEIGHT + 40;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={rows}
        layout="vertical"
        barSize={BAR_SIZE}
        margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={(v) => `${v}h`}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="task"
          width={LABEL_WIDTH}
          interval={0}
          tick={<TaskTick fill={theme.palette.text.primary} />}
          tickLine={false}
        />
        <Tooltip content={<DurationTooltip />} cursor={{ fill: theme.palette.action.hover }} />
        <Bar
          dataKey="duration"
          fill={theme.palette.primary.main}
          radius={[0, 4, 4, 0]}
          style={onBarClick ? { cursor: "pointer" } : undefined}
          onClick={(bar) => {
            const row = bar?.payload;
            if (onBarClick && row?.task_id) onBarClick(row);
          }}
        >
          <LabelList
            dataKey="duration"
            position="right"
            formatter={formatFloatToTimeHM}
            style={{ fontSize: 12, fill: theme.palette.text.secondary }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const DashboardCard = ({ title, type, data, columns, onRowClick }) => {
  return (
    <Card variant="outlined" sx={{ margin: 2, padding: 0 }}>
      <CardContent>
        <Typography variant="h6">{title}</Typography>
        {type === "bar" ? (
          <TimeBarChart data={data} onBarClick={onRowClick} />
        ) : type === "Line" ? (
          <LineChart
            width={500}
            height={300}
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="pv"
              stroke="#8884d8"
              activeDot={{ r: 8 }}
            />
            <Line type="monotone" dataKey="uv" stroke="#82ca9d" />
          </LineChart>
        ) : type === "list" ? (
          <ListComponent data={data} columns={columns} onRowClick={onRowClick} />
        ) : (
          <Box>
            <Typography variant="h6">No Charts Available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardCard;
